import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";

import { JSX } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import Icon from "@/components/common/Icon/Icon.tsx";
import ModalProvider from "@/components/helpers/Modal.tsx";
import Spinner from "@/components/helpers/Spinner/Spinner";
import { IconSignOut } from "@/components/icons";
import HeaderProfileProfiles from "@/components/layout/Header/HeaderProfile/HeaderProfileProfiles.tsx";
import QuitModal from "@/components/modals/QuitModal.tsx";

import { ROUTES } from "@/config/routes.ts";
import { useSelectProfileList } from "@/hooks/api/login.hooks";
import { useGetProfileDetail } from "@/hooks/api/user.hooks";
import { cn } from "@/utils/cn.ts";

const HeaderProfile = (): JSX.Element => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { data } = useSelectProfileList();

  const id = localStorage.getItem("splay:profileId");

  const { data: profileData, isLoading } = useGetProfileDetail(id ? Number(id) : 0);
  let timer: ReturnType<typeof setTimeout>;

  const openMenu = (isOpen: boolean, target: EventTarget): void => {
    isOpen ? clearTimeout(timer) : (target as HTMLButtonElement).click();
  };

  const closeMenu = (close: () => void): void => {
    timer = setTimeout(() => {
      close();
    }, 300);
  };

  return (
    <div className="relative flex text-left">
      <Menu>
        {({ open, close }) => (
          <>
            <MenuButton
              type={"button"}
              aria-label={"Открыть меню"}
              onMouseOver={({ target }) => openMenu(open, target)}
              onMouseOut={() => closeMenu(close)}
              className={"h-10 w-10 overflow-hidden rounded-full"}
            >
              {!isLoading ? (
                <img
                  src={profileData.picture?.resized || "/images/anonymous.png"}
                  style={{ background: profileData.picture?.color || profileData.picture ? "#FF4B00" : "" }}
                  alt={"User"}
                  loading={"lazy"}
                  className={"h-10 w-10 object-cover object-center"}
                />
              ) : (
                <Spinner className="size-6 fill-on-primary" />
              )}
            </MenuButton>
            <MenuItems
              modal={false}
              transition
              onMouseOver={() => clearTimeout(timer)}
              onMouseOut={() => closeMenu(close)}
              className={
                "absolute right-0 top-0 mt-[45px] w-64 origin-top-right space-y-2 rounded-[0.625rem] bg-surface-bright pb-2 pt-3 text-on-primary outline-none ring-0 transition duration-300 data-[closed]:scale-95 data-[closed]:opacity-0"
              }
            >
              <HeaderProfileProfiles data={data.data} />
              <ul>
                <li>
                  <MenuItem>
                    <Link
                      to={ROUTES.GATE.PROFILES}
                      aria-label={t("Profile.manage")}
                      className={
                        "flex w-full items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-container-high"
                      }
                    >
                      <Icon
                        name={"profile"}
                        className={cn("size-6 fill-on-surface transition-all ", {
                          "fill-on-primary": pathname.includes(ROUTES.GATE.MAIN),
                        })}
                      />
                      {t("Profile.manage")}
                    </Link>
                  </MenuItem>
                </li>

                <li>
                  <MenuItem>
                    <Link
                      to={ROUTES.STAFF.USER_SETTINGS}
                      aria-label={t("settings")}
                      className={
                        "hidden w-full items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-container-high lg:flex"
                      }
                    >
                      <Icon
                        name={"settings"}
                        className={cn("size-6 fill-on-surface transition-all ", {
                          "fill-on-primary": pathname.includes(ROUTES.STAFF.MAIN),
                        })}
                      />
                      {t("settings")}
                    </Link>
                  </MenuItem>
                  <MenuItem>
                    <Link
                      to={ROUTES.STAFF.NAVIGATIONS}
                      aria-label={t("settings")}
                      className={
                        "flex w-full items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-container-high lg:hidden"
                      }
                    >
                      <Icon
                        name={"settings"}
                        className={cn("size-6 fill-on-surface transition-all ", {
                          "fill-on-primary": pathname.includes(ROUTES.STAFF.MAIN),
                        })}
                      />
                      {t("settings")}
                    </Link>
                  </MenuItem>
                </li>
              </ul>
              <hr className="border-white-opacity-1" />
              <MenuItem>
                <ModalProvider<boolean> initialState={false}>
                  {({ modalState, setModalState, closeModal }) => (
                    <>
                      <button
                        type={"button"}
                        aria-label={t("auth.sign-out-title")}
                        onClick={() => setModalState(true)}
                        className={
                          "flex w-full items-center gap-3 px-4 py-3 text-[#BB191A] transition-colors hover:bg-surface-container-high"
                        }
                      >
                        <IconSignOut className="size-6 stroke-[#BB191A]" />
                        {t("auth.sign-out")}
                      </button>
                      <QuitModal isOpen={modalState} onClose={closeModal} />
                    </>
                  )}
                </ModalProvider>
              </MenuItem>
            </MenuItems>
          </>
        )}
      </Menu>
    </div>
  );
};

export default HeaderProfile;
